
import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { PatientSourceData } from '@/types/statistics';

interface PatientSourceChartProps {
  data: PatientSourceData;
}

const COLORS = ['#8B5CF6', '#0EA5E9', '#10B981', '#F59E0B', '#EC4899'];

const RADIAN = Math.PI / 180;
const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index }: any) => {
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text x={x} y={y} fill="white" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central">
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

const PatientSourceChart: React.FC<PatientSourceChartProps> = ({ data }) => {
  const chartData = [
    { name: 'Organic Profile Views', value: data.organicViews },
    { name: 'Sponsored Ads', value: data.sponsoredAds },
    { name: 'Clinic Referrals', value: data.clinicReferrals },
    { name: 'Redistributed Cases', value: data.redistributedCases },
    { name: 'Other Sources', value: data.otherSources }
  ].filter(item => item.value > 0); // Only show sources with values > 0

  return (
    <div className="w-full">
      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={renderCustomizedLabel}
              outerRadius={80}
              fill="#8884d8"
              dataKey="value"
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip 
              formatter={(value, name) => [`${value} patients`, name]}
              contentStyle={{ 
                backgroundColor: 'white', 
                borderRadius: '8px',
                boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
                border: '1px solid #eaeaea' 
              }}
            />
            <Legend 
              layout="horizontal" 
              verticalAlign="bottom" 
              align="center" 
            />
          </PieChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-6">
        {chartData.map((entry, index) => (
          <div key={index} className="flex flex-col items-center justify-center">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[index % COLORS.length] }}></div>
            <p className="text-xs text-center mt-1">{entry.name}</p>
            <p className="text-sm font-medium">{entry.value} ({((entry.value / data.total) * 100).toFixed(1)}%)</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PatientSourceChart;
